import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { formatDateTimeMX } from '@/shared/lib/dateUtils'
import { useState } from 'react'
import { RefreshCw, ChevronDown, Clock, Monitor, X, AlertCircle } from 'lucide-react'
import { useAuditHistory } from '@/hooks/useAuditHistory'
import { Badge, type ColorDeBadge } from '@/shared/components/Badge'
import {
  getActionConfig, 
  getEntityConfig,
  formatRelativeTime,
  generateAuditDescription,
  formatJsonForDisplay
} from '@/features/rh/lib/auditUtils'

const colorDeMetodo = (metodo?: string | null): ColorDeBadge => {
  switch (metodo) {
    case 'GET': return 'azul'
    case 'POST': return 'verde'
    case 'PUT': return 'amarillo'
    case 'DELETE': return 'rojo'
    default: return 'gris'
  }
}

const colorDeCodigo = (codigo?: number | null): ColorDeBadge => {
  if (codigo != null && codigo >= 200 && codigo < 300) return 'verde'
  if (codigo != null && codigo >= 400) return 'rojo'
  return 'amarillo'
}

interface AuditHistoryProps {
  userId: string
}

const AuditHistory = ({ userId }: AuditHistoryProps) => {
  const { 
    auditLogs, 
    isLoading, 
    error, 
    hasMore, 
    totalLogs, 
    loadMore, 
    refresh,
    clearError 
  } = useAuditHistory({ userId })

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRowExpanded = (logId: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId)
    } else {
      newExpanded.add(logId)
    }
    setExpandedRows(newExpanded)
  }

  const handleRefresh = async () => {
    clearError()
    await refresh()
  }

  // Filtrar logs válidos (logs que tienen los campos requeridos)
  const validLogs = auditLogs.filter(log => 
    log && 
    log.id && 
    typeof log.id === 'string' &&
    log.timestamp &&
    typeof log.timestamp === 'string'
  )

  if (isLoading && auditLogs.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Clock className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Historial de Actividad</h2>
              <p className="text-sm text-gray-600">Acciones recientes del usuario y eventos del sistema</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-600">Cargando historial de actividad...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Clock className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Historial de Actividad</h2>
              <p className="text-sm text-gray-600">
                {totalLogs > 0 ? `${totalLogs} actividades recientes` : 'Acciones recientes del usuario y eventos del sistema'}
              </p>
            </div>
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className={estiloDeBoton('secundario')}
            aria-label="Actualizar Historial de Actividad"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Mensaje de Error */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2">
            <X className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Contenido */}
      <div className="p-6">
        {validLogs.length === 0 ? (
          // Estado Vacío
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No se encontró historial de actividad</p>
            <p className="text-sm text-gray-400">Las acciones del usuario aparecerán aquí cuando estén disponibles</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tabla para Escritorio */}
            <div className="hidden md:block">
              <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E5E7EB]">
                  <thead className="bg-[#FAF9FF]">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acción</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Entidad</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Detalles</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Tiempo</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E1E1E1]">
                    {validLogs.map((log) => {
                      const actionConfig = getActionConfig(log.actionDescription)
                      const entityConfig = getEntityConfig(log.module)
                      const isExpanded = expandedRows.has(log.id)
                      const ActionIcon = actionConfig.icon
                      const EntityIcon = entityConfig.icon

                      return (
                        <>
                          <tr key={log.id} className="hover:bg-[#F4F7FB] transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-lg ${actionConfig.bgColor}`}>
                                  <ActionIcon className={`w-4 h-4 ${actionConfig.color}`} />
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                  {actionConfig.label}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <EntityIcon className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-900">{entityConfig.label}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-600 max-w-xs truncate">
                                {generateAuditDescription(log)}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-500">
                                {formatRelativeTime(log.timestamp)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => toggleRowExpanded(log.id)}
                                className="p-1 rounded hover:bg-gray-100"
                              >
                                <ChevronDown 
                                  className={`w-4 h-4 text-gray-400 transition-transform ${
                                    isExpanded ? 'rotate-180' : ''
                                  }`} 
                                />
                              </button>
                            </td>
                          </tr>
                          {/* Fila de detalles expandidos */}
                          {isExpanded && (
                            <tr key={`${log.id}-details`}>
                              <td colSpan={5} className="py-0">
                                <div className="bg-gray-50 px-4 py-4 border-b border-gray-200">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="font-medium text-gray-700">Marca de tiempo:</span>
                                      <p className="text-gray-600">
                                        {formatDateTimeMX(log.timestamp, { segundos: true })}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Ruta:</span>
                                      <p className="text-gray-600 font-mono">{log.apiRoute || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Método:</span>
                                      <p className="text-gray-600">
                                        <Badge color={colorDeMetodo(log.httpMethod)}>{log.httpMethod || 'N/A'}</Badge>
                                      </p>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Estado:</span>
                                      <p className="text-gray-600">
                                        <Badge color={colorDeCodigo(log.statusCode)}>{log.statusCode || 'N/A'}</Badge>
                                      </p>
                                    </div> 
                                    {log.requestData && (
                                      <div className="md:col-span-2">
                                        <span className="font-medium text-gray-700">Datos de Solicitud:</span>
                                        <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                          <pre className="whitespace-pre-wrap text-gray-800">
                                            {formatJsonForDisplay(log.requestData)}
                                          </pre>
                                        </div>
                                      </div>
                                    )}
                                    {log.responseData && (
                                      <div className="md:col-span-2">
                                        <span className="font-medium text-gray-700">Datos de Respuesta:</span>
                                        <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                          <pre className="whitespace-pre-wrap text-gray-800">
                                            {formatJsonForDisplay(log.responseData)}
                                          </pre>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            </div>

            {/* Tarjetas para Móvil */}
            <div className="md:hidden space-y-4">
              {validLogs.map((log) => {
                const actionConfig = getActionConfig(log.actionDescription)
                const entityConfig = getEntityConfig(log.module)
                const isExpanded = expandedRows.has(log.id)
                const ActionIcon = actionConfig.icon
                const EntityIcon = entityConfig.icon

                return (
                  <div key={log.id} className="border border-gray-200 rounded-lg">
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className={`p-2 rounded-lg ${actionConfig.bgColor} flex-shrink-0`}>
                            <ActionIcon className={`w-4 h-4 ${actionConfig.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {actionConfig.label}
                              </span>
                              <EntityIcon className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">{entityConfig.label}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                              {generateAuditDescription(log)}
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{formatRelativeTime(log.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleRowExpanded(log.id)}
                          className="p-1 rounded hover:bg-gray-100 ml-2"
                        >
                          <ChevronDown 
                            className={`w-4 h-4 text-gray-400 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>
                      </div>
                    </div>
                    
                    {/* Detalles expandidos para móvil */}
                    {isExpanded && (
                      <div className="bg-gray-50 px-4 py-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Marca de tiempo:</span>
                            <p className="text-gray-600">
                              {formatDateTimeMX(log.timestamp, { segundos: true })}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Ruta:</span>
                            <p className="text-gray-600 font-mono">{log.apiRoute || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Método:</span>
                            <p className="text-gray-600">
                              <Badge color={colorDeMetodo(log.httpMethod)}>{log.httpMethod || 'N/A'}</Badge>
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Estado:</span>
                            <p className="text-gray-600">
                              <Badge color={colorDeCodigo(log.statusCode)}>{log.statusCode || 'N/A'}</Badge>
                            </p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Dirección IP:</span>
                            <p className="text-gray-600 font-mono">{log.ipAddress}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Agente de Usuario:</span>
                            <p className="text-gray-600 truncate" title={log.userAgent}>
                              <Monitor className="w-4 h-4 inline mr-1" />
                              {log.userAgent?.split(' ')[0] || 'N/A'}
                            </p>
                          </div>
                          {log.requestData && (
                            <div>
                              <span className="font-medium text-gray-700">Datos de Solicitud:</span>
                              <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                <pre className="whitespace-pre-wrap text-gray-800">
                                  {formatJsonForDisplay(log.requestData)}
                                </pre>
                              </div>
                            </div>
                          )}
                          {log.responseData && (
                            <div>
                              <span className="font-medium text-gray-700">Datos de Respuesta:</span>
                              <div className="mt-1 bg-gray-100 rounded p-3 text-xs font-mono overflow-x-auto">
                                <pre className="whitespace-pre-wrap text-gray-800">
                                  {formatJsonForDisplay(log.responseData)}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Botón Cargar Más */}
            {hasMore && (
              <div className="text-center pt-4">
                <button
                  onClick={loadMore}
                  disabled={isLoading}
                  className="elevar px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Cargando...</span>
                    </div>
                  ) : (
                    'Cargar Más'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AuditHistory